import { cn } from "@/lib/utils";
import { ReactNode } from "react";

export enum FontWeight {
	Normal = 'font-normal',
	Medium = 'font-medium',
	Semibold = 'font-semibold',
	Bold = 'font-bold',
}

export enum TextSize {
	ExtraSmall = 'text-xs',
	Small = 'text-sm',
	Medium = 'text-base',
	Large = 'text-lg',
}

export enum FontColor {
	Gray1000 = 'text-gray-1000',
	Gray800 = 'text-gray-800',
	Gray600 = 'text-gray-600',
	Gray500 = 'text-gray-500',
	Gray400 = 'text-gray-400',
	Gray200 = 'text-gray-200',
}

interface TypographyProps {
	children: ReactNode;
	className?: string;
	weight?: FontWeight;
	color?: FontColor;
}

interface BodyProps extends TypographyProps {
	size?: TextSize;
	as?: 'p' | 'span' | 'th' | 'td' | 'div';
}

export function H1({ children, className }: TypographyProps) {
	return (
		<h1 className={cn("text-3xl font-bold text-palette-black", className)}>
			{children}
		</h1>
	);
}

export function H2({ children, className }: TypographyProps) {
	return (
		<h2 className={cn("text-xl font-semibold text-palette-black", className)}>
			{children}
		</h2>
	);
}

export function H3({ children, className }: TypographyProps) {
	return (
		<h3 className={cn("text-lg font-semibold text-palette-black", className)}>
			{children}
		</h3>
	);
}

export function Body({ children, className, size = TextSize.Medium, weight = FontWeight.Normal, color = FontColor.Gray800, as = "p"
}: BodyProps) {
	const Component = as;
	return (
		<Component className={cn(size, weight, color, className)}> {children} </Component>
	);
}

function Typography({ children, className, size = TextSize.Medium, weight = FontWeight.Normal, color = FontColor.Gray1000, as = "p"
}: BodyProps) {
	const Component = as;
	return (
		<Component className={cn(size, weight, color, className)} > {children} </Component>
	);
}

//HEADER
export function H4(props: Omit<BodyProps, 'size' | 'weight'>) {
	return (
		<Typography size={TextSize.Large} weight={FontWeight.Bold} {...props} />
	);
}

//BODY
export function Body0(props: Omit<BodyProps, 'size' | 'weight'>) {
	return (
		<Typography size={TextSize.Medium} weight={FontWeight.Bold} {...props} />
	);
}

export function Body1(props: Omit<BodyProps, 'size' | 'weight' | 'color'>) {
	return (
		<Typography size={TextSize.Medium} weight={FontWeight.Normal} {...props} />
	);
}

export function Body1Muted(props: Omit<BodyProps, 'size' | 'weight' | 'color'>) {
	return (
		<Typography size={TextSize.Medium} weight={FontWeight.Normal} color={FontColor.Gray400} {...props} />
	);
}

export function Body2(props: Omit<BodyProps, 'size' | 'weight' | 'color'>) {
	return (
		<Typography size={TextSize.Small} weight={FontWeight.Normal} {...props} />
	);
}

//META
export function Meta1(props: Omit<BodyProps, 'size' | 'weight' | 'color'>) {
	return (
		<Typography size={TextSize.Small} weight={FontWeight.Medium} color={FontColor.Gray500} {...props} />
	);
}

